import { revalidateLogic } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { deleteCollectionItemModal } from "@/components/admin/DeleteModal";
import { RequirePermission } from "@/components/admin/RequirePermission";
import { LoadingFallback } from "@/components/fallbacks";
import { InputFile } from "@/components/form/input-file";
import { PhoneInput } from "@/components/form/phone-input";
import { useAppForm } from "@/components/form/tanstack-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useActiveBranch } from "@/hooks/use-active-branch";
import { useFile } from "@/hooks/use-file";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { pb } from "@/lib/pocketbase";
import { fileToAPI } from "@/lib/utils";
import { useRemoveDoctors } from "@/modules/doctors/use-remove-doctors";
import { useSession } from "@/providers/session-provider";
import { Collections, type Create, type Update } from "@/types/pocketbase.ts";
import { branchesQueries } from "./queries";
import { type BranchFormValues, branchFormSchema } from "./schema";

type BranchFormProps = {
	onSuccess?: () => void;
	onCancel?: () => void;
	id?: string;
	readOnly?: boolean;
};

export const BranchForm = ({ onSuccess, id, readOnly }: BranchFormProps) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { setActiveBranch } = useActiveBranch();
	const { isBlocked, showBlockedModal } = useSubscriptionStatus();
	const { removeDoctors } = useRemoveDoctors({ branchId: id });

	const branch = useQuery(branchesQueries.one({ id }));

	const sesssion = useSession();
	const currentUserId = sesssion?.id;

	const createBranch = useMutation({
		mutationFn: ({ payload }: { payload: BranchFormValues }) => {
			toast.loading(
				t(($) => $.modules.branches.forms.create.status.loading.title),
				{
					id: "create-branch",
					description: t(($) => $.modules.branches.forms.create.status.loading.message),
				}
			);

			const values: Create<"branches"> = {
				...payload,
				image: fileToAPI(payload.image),
			};

			return pb.collection(Collections.Branches).create(values);
		},
		onSuccess: (payload) => {
			toast.success(
				t(($) => $.modules.branches.forms.create.status.success.title),
				{
					id: "create-branch",
					description: t(($) => $.modules.branches.forms.create.status.success.message),
				}
			);

			setActiveBranch(payload.id);

			queryClient.invalidateQueries({
				queryKey: branchesQueries.all(),
			});

			onSuccess?.();
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, defaultValues);

			if (parsed.unknown) {
				return toast.error(
					t(($) => $.modules.branches.forms.create.status.error.title),
					{
						id: "create-branch",
						description: parsed.message || t(($) => $.modules.branches.forms.create.status.error.message),
					}
				);
			}

			form.setErrorMap({ onDynamic: parsed.errorMap });
			toast.dismiss("create-branch");
		},
	});

	const updateBranch = useMutation({
		mutationFn: ({ branchId, payload }: { payload: BranchFormValues; branchId: string }) => {
			toast.loading(
				t(($) => $.modules.branches.forms.edit.status.loading.title),
				{
					id: "update-branch",
					description: t(($) => $.modules.branches.forms.edit.status.loading.message),
				}
			);

			const values: Update<"branches"> = {
				...payload,
				image: fileToAPI(payload.image),
			};

			return pb.collection(Collections.Branches).update(branchId, values);
		},
		onSuccess: () => {
			toast.success(
				t(($) => $.modules.branches.forms.edit.status.success.title),
				{
					id: "update-branch",
					description: t(($) => $.modules.branches.forms.edit.status.success.message),
				}
			);

			queryClient.invalidateQueries({
				queryKey: branchesQueries.all(),
			});

			onSuccess?.();
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, defaultValues);

			if (parsed.unknown) {
				return toast.error(
					t(($) => $.modules.branches.forms.edit.status.error.title),
					{
						id: "update-branch",
						description: parsed.message || t(($) => $.modules.branches.forms.edit.status.error.message),
					}
				);
			}

			form.setErrorMap({ onDynamic: parsed.errorMap });
			toast.dismiss("update-branch");
		},
	});

	const onLeave = useCallback(() => {
		if (!currentUserId) return;

		removeDoctors({
			ids: [currentUserId],
			onSuccess,
		});
	}, [currentUserId, removeDoctors, onSuccess]);

	const onDelete = useCallback(() => {
		if (!id) return;

		if (isBlocked) {
			showBlockedModal();
			return;
		}

		deleteCollectionItemModal.open({
			items: [{ collection: Collections.Branches, id }],
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: branchesQueries.all(),
				});

				onSuccess?.();
			},
		});
	}, [id, isBlocked, showBlockedModal, queryClient, onSuccess]);

	const file = useFile();

	const imageUrl = file.getUrl({
		collectionName: "Branches",
		id: branch.data?.id,
		filename: branch.data?.image,
	});

	const defaultValues: BranchFormValues = {
		name: branch.data?.name ?? "",
		address: branch.data?.address ?? "",
		email: branch.data?.email ?? "",
		image: imageUrl,
		phoneNumber: branch.data?.phoneNumber ?? "",
	};

	const form = useAppForm({
		defaultValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: { onDynamic: branchFormSchema },
		onSubmit: ({ value: payload }) => {
			if (id) {
				updateBranch.mutate({ payload, branchId: id });
				return;
			}

			createBranch.mutate({ payload });
		},
	});

	const handleSubmit = useCallback(
		(e: React.SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form]
	);

	if (branch.isLoading) {
		return <LoadingFallback title="Loading" />;
	}

	return (
		<form.AppForm>
			<form className="grid gap-4" onSubmit={handleSubmit}>
				<form.AppField name="image">
					{(field) => (
						<field.Field>
							<field.FieldLabel>{t(($) => $.modules.branches.forms.fields.image.label)}</field.FieldLabel>
							<field.FieldControl>
								<InputFile
									disabled={readOnly}
									multiple={false}
									onChange={field.handleChange}
									value={field.state.value}
								/>
							</field.FieldControl>

							<field.FieldError />
						</field.Field>
					)}
				</form.AppField>

				<form.AppField name="name">
					{(field) => (
						<field.Field>
							<field.FieldLabel>{t(($) => $.modules.branches.forms.fields.name.label)}</field.FieldLabel>
							<field.FieldControl>
								<Input
									disabled={readOnly}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder={t(($) => $.modules.branches.forms.fields.name.placeholder)}
									value={field.state.value}
								/>
							</field.FieldControl>

							<field.FieldError />
						</field.Field>
					)}
				</form.AppField>

				<form.AppField name="email">
					{(field) => (
						<field.Field>
							<field.FieldLabel>{t(($) => $.modules.branches.forms.fields.email.label)}</field.FieldLabel>
							<field.FieldControl>
								<Input
									disabled={readOnly}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder={t(($) => $.modules.branches.forms.fields.email.placeholder)}
									type="email"
									value={field.state.value}
								/>
							</field.FieldControl>

							<field.FieldError />
						</field.Field>
					)}
				</form.AppField>

				<form.AppField name="address">
					{(field) => (
						<field.Field>
							<field.FieldLabel>{t(($) => $.modules.branches.forms.fields.address.label)}</field.FieldLabel>
							<field.FieldControl>
								<Input
									disabled={readOnly}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder={t(($) => $.modules.branches.forms.fields.address.placeholder)}
									value={field.state.value}
								/>
							</field.FieldControl>

							<field.FieldError />
						</field.Field>
					)}
				</form.AppField>

				<form.AppField name="phoneNumber">
					{(field) => (
						<field.Field>
							<field.FieldLabel>{t(($) => $.modules.branches.forms.fields.phoneNumber.label)}</field.FieldLabel>
							<field.FieldControl>
								<PhoneInput
									disabled={readOnly}
									onBlur={field.handleBlur}
									onChange={(value) => field.handleChange(value)}
									placeholder={t(($) => $.modules.branches.forms.fields.phoneNumber.placeholder)}
									value={field.state.value}
								/>
							</field.FieldControl>

							<field.FieldError />
						</field.Field>
					)}
				</form.AppField>

				<div className="flex items-center justify-end gap-4">
					{readOnly ? (
						<Button className="mr-auto" onClick={onLeave} type="button" variant="outline">
							{t(($) => $.common.actions.leave)}
						</Button>
					) : (
						<>
							{id && (
								<RequirePermission action={"delete"} collection={Collections.Branches}>
									<Button className="mr-auto" onClick={onDelete} type="button" variant="destructive">
										{t(($) => $.common.actions.delete)}
									</Button>
								</RequirePermission>
							)}

							<RequirePermission action={id ? "edit" : "create"} collection={Collections.Branches}>
								<Button disabled={updateBranch.isPending || createBranch.isPending || readOnly} type="submit">
									{t(($) => $.common.actions.save)}
								</Button>
							</RequirePermission>
						</>
					)}
				</div>
			</form>
		</form.AppForm>
	);
};
